"use client"
import IconFacebook from "@/app/(site)/_components/Atoms/Icons/IconFacebook"
import IconInstagram from "@/app/(site)/_components/Atoms/Icons/IconInstagram"
import IconLinkedIn from "@/app/(site)/_components/Atoms/Icons/IconLinkedIn"
import IconStrava from "@/app/(site)/_components/Atoms/Icons/IconStrava"
import IconYouTube from "@/app/(site)/_components/Atoms/Icons/IconYouTube"
import { FooterMenuItems } from "./FooterMenuItems"

import {
    getLangCode,
    getMarketCodeFromCentraLocale,
    isGeneralLinkDefined
} from "@/app/(site)/_utils/misc"
import { CentraLocaleShippingCountryCode } from "@/data/centra-locale-shipping-country-code"
import { navigationEvent } from "@/lib/utils/tracking/gtmEvents"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import centraMarketToCentraArbitraryShippingCountry from "@/maps/centra-market-to-centra-arbitrary-shipping-country"
import { t } from "@/translations"
import { GeneralLink, SiteConfig } from "@/types"
import Link from "next/link"
import { Fragment } from "react"
import BottomFooter from "./BottomFooter"
import ChangeCountryLink from "./ChangeCountryLink"
import KlaviyoSignupForm from "./KlaviyoSignupForm"

type FooterProps = {
    config: SiteConfig | undefined
    params: { locale: string }
}

export default function Footer({ config, params }: FooterProps) {
    const langCode = getLangCode(params.locale) || "en"
    const siteContext = useSiteContext()
    const marketCode = getMarketCodeFromCentraLocale(params.locale)
    // Fetch product data from Centra using the token
    const countryCode: CentraLocaleShippingCountryCode =
        centraMarketToCentraArbitraryShippingCountry.get(
            marketCode
        ) as CentraLocaleShippingCountryCode

    function getGeneralFooterLinkHref(link: GeneralLink) {
        const href =
            link.external ||
            link.internal ||
            (link.categoryUri && `/categories/${link.categoryUri}`) ||
            (link.productDisplayUri && `/product/${link.productDisplayUri}`) ||
            undefined

        if (typeof href === "undefined") return href
        if (link.external) return href

        return href.startsWith("/") ? href : `/${href}`
    }

    const handleSocialMediaClick = (platform: string, url: string) => {
        const clickPath = `footer|social_media|${platform}`

        navigationEvent(url, "footer", siteContext, clickPath)
    }

    return (
        <footer className="bg-black text-[12px] text-white">
            <div className="px-16 pt-64 uppercase md:px-24 md:pb-[288px] md:pt-72">
                <div className="mb-32 grid grid-cols-1 gap-32 md:grid-cols-6 lg:mb-0">
                    <div className="col-span-2 mr-0 lg:mr-100">
                        <div className="mb-16 flex items-center gap-2">
                            <FooterMenuItems
                                level="top"
                                label={t(langCode, "footer.newsletter_subscribe")}
                                light
                            />
                        </div>
                        <KlaviyoSignupForm
                            langCode={langCode}
                            countryCode={countryCode}
                            locale={params.locale}
                        />
                    </div>
                    {config?.footerNavigation
                        ?.filter(group => group?.pages?.length > 0)
                        .map((group, index) => (
                            <div key={index} className="col-span-2 md:col-span-1">
                                <div className="mb-16 flex items-center gap-2">
                                    <FooterMenuItems
                                        level="top"
                                        label={group?.title}
                                        light
                                    />
                                </div>
                                <div className="border-l-2 border-white pl-12">
                                    <nav>
                                        <ul className="flex flex-col gap-8">
                                            {group?.pages
                                                ?.filter(
                                                    page =>
                                                        page.title &&
                                                        isGeneralLinkDefined(page)
                                                )
                                                .map((page, index) => (
                                                    <Fragment
                                                        key={`${page._key}-${index}`}
                                                    >
                                                        <FooterMenuItems
                                                            level="second"
                                                            to={
                                                                getGeneralFooterLinkHref(
                                                                    page
                                                                )!
                                                            }
                                                            label={page.title}
                                                            light
                                                        />
                                                    </Fragment>
                                                ))}
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        ))}
                    <div className="col-span-2 md:col-span-1">
                        <div className="mb-16 flex items-center gap-2">
                            <FooterMenuItems
                                level="top"
                                label={t(langCode, "footer.social_media")}
                                aria-label={t(langCode, "footer.social_media")}
                                light
                            />
                        </div>
                        <ul className="flex flex-wrap gap-16">
                            <li>
                                <div
                                    onClick={() =>
                                        handleSocialMediaClick(
                                            "facebook",
                                            "https://www.facebook.com/poc"
                                        )
                                    }
                                >
                                    <Link
                                        href={"https://www.facebook.com/poc"}
                                        target="_blank"
                                    >
                                        <IconFacebook
                                            className="h-24 w-24"
                                            aria-label={t(langCode, "footer.facebook")}
                                        />
                                    </Link>
                                </div>
                            </li>
                            <li>
                                <div
                                    onClick={() =>
                                        handleSocialMediaClick(
                                            "linkedin",
                                            "https://www.linkedin.com/company/pocsports"
                                        )
                                    }
                                >
                                    <Link
                                        href={
                                            "https://www.linkedin.com/company/pocsports"
                                        }
                                        target="_blank"
                                    >
                                        <IconLinkedIn
                                            className="h-24 w-24"
                                            aria-label={t(langCode, "footer.linkedin")}
                                        />
                                    </Link>
                                </div>
                            </li>
                            <li>
                                <div
                                    onClick={() =>
                                        handleSocialMediaClick(
                                            "strava",
                                            "https://www.strava.com/clubs/244516"
                                        )
                                    }
                                >
                                    <Link
                                        href={"https://www.strava.com/clubs/244516"}
                                        target="_blank"
                                    >
                                        <IconStrava
                                            className="h-24 w-24"
                                            aria-label={t(langCode, "footer.strava")}
                                        />
                                    </Link>
                                </div>
                            </li>
                            <li>
                                <div
                                    onClick={() =>
                                        handleSocialMediaClick(
                                            "youtube",
                                            "https://www.youtube.com/channel/UCm55EDQIcVbwUdE48nFPGWQ"
                                        )
                                    }
                                >
                                    <Link
                                        href={
                                            "https://www.youtube.com/channel/UCm55EDQIcVbwUdE48nFPGWQ"
                                        }
                                        target="_blank"
                                    >
                                        <IconYouTube
                                            className="h-24 w-24"
                                            aria-label={t(langCode, "footer.youtube")}
                                        />
                                    </Link>
                                </div>
                            </li>
                            <li>
                                <div
                                    onClick={() =>
                                        handleSocialMediaClick(
                                            "instagram",
                                            "https://www.instagram.com/pocsports"
                                        )
                                    }
                                >
                                    <Link
                                        href={"https://www.instagram.com/pocsports"}
                                        target="_blank"
                                    >
                                        <IconInstagram
                                            className="h-24 w-24"
                                            aria-label={t(langCode, "footer.instagram")}
                                        />
                                    </Link>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="flex w-[fit-content] flex-col">
                    {/* <!-- CookiePro Cookies Settings button start --> */}
                    <button
                        id="ot-sdk-btn"
                        className="ot-sdk-show-settings !hover:border-opacity-90 !focus:outline-none mb-16 !inline-flex inline-flex !items-center items-center !justify-center justify-center !gap-8 gap-8 !rounded-full rounded-full !border border !border-white border-white !bg-transparent bg-transparent !px-16 !py-16 px-16 py-16 !font-trimRegular font-trimRegular !text-16 text-16 !font-light font-light !uppercase uppercase !leading-[15px] leading-[15px] !text-white text-white !transition transition !duration-300 duration-300 !ease-in-out ease-in-out hover:border-opacity-90 focus:outline-none"
                        onClick={() => {
                            const clickPath = "footer|cookie_preferences"

                            navigationEvent(
                                "cookie_preferences",
                                "footer",
                                siteContext,
                                clickPath
                            )
                        }}
                    >
                        {t(langCode, "footer.cookie_preferences")}
                        {/* Text/label will come from cookiepro settings, this here is just a fallback */}
                    </button>
                    {/* <!-- CookiePro Cookies Settings button end --> */}
                    <ChangeCountryLink langCode={langCode} />
                </div>
            </div>
            <BottomFooter />
        </footer>
    )
}
